import { BizScoring } from "@/components/business/BizScoring";
import { BizBreakeven } from "@/components/business/BizBreakeven";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, CalendarClock, Landmark, Loader2, Repeat, ShieldCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Area, Bar, CartesianGrid, ComposedChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { C, ChartTooltip, Grad, anim, axis, cursor } from "@/components/common/ChartKit";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { SetupCard } from "@/components/setup/SetupCard";
import { InterCompany } from "@/components/business/InterCompany";
import { FiscalCalendar } from "@/components/business/FiscalCalendar";

const monthShort = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "short" });

const Kpi = ({ label, value, sub, testid, to }) => (
  <Link to={to} className="card-surface block p-4 transition-transform hover:-translate-y-0.5" data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div><div className="num mt-1 text-lg font-bold sm:text-2xl">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </Link>
);

export const ConnectBank = ({ company }) => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (company.bank_connected || company.my_role === "viewer") return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/business/companies/${company.id}/bridge/connect`); toast.success(data.message); qc.invalidateQueries(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card-surface tone-wash tone-sky flex flex-wrap items-center gap-3 p-5" data-testid="biz-connect-bank">
      <Landmark className="h-5 w-5" /><div className="min-w-0 flex-1"><div className="font-semibold">Connectez les comptes professionnels</div>
        <p className="text-xs text-muted-foreground">Connexion sécurisée en lecture seule (agrégateur agréé ACPR) : les opérations sont importées et catégorisées automatiquement, aucun virement possible.</p></div>
      <Button className="rounded-full" onClick={go} disabled={busy} data-testid="biz-connect-bank-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Connecter la banque</Button>
    </section>
  );
};

export const AlertsList = ({ alerts, threshold }) => {
  const { money } = useFormat();
  if (!alerts.length) return <p className="text-sm text-muted-foreground" data-testid="biz-alerts-empty">Aucune variation au-delà de {threshold} % ce mois-ci.</p>;
  return (
    <ul className="space-y-2" data-testid="biz-alerts">
      {alerts.map((a) => (
        <li key={a.key} className="flex items-center gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-3 text-sm" data-testid={`biz-alert-${a.key}`}>
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="min-w-0 flex-1"><b>{a.label}</b> <span className="text-xs text-muted-foreground">{a.kind === "category" ? "poste" : "fournisseur"}</span>
            <span className="block text-xs text-muted-foreground"><span className="num">{money(a.spent, { maximumFractionDigits: 0 })}</span> ce mois contre <span className="num">{money(a.avg6, { maximumFractionDigits: 0 })}</span> de moyenne sur 6 mois</span></span>
          <span className="num font-bold text-destructive">+{a.delta_pct} %</span>
        </li>
      ))}
    </ul>
  );
};

export const BizDashboard = ({ company }) => {
  const { money, date } = useFormat();
  const { data: d } = useQuery({ queryKey: ["biz-dashboard", company.id], queryFn: () => api.get(`/business/companies/${company.id}/dashboard`).then((r) => r.data) });
  if (!d) return <Skeleton className="h-72 rounded-2xl" />;
  const o = d.overview;
  const m0 = (v) => money(v, { maximumFractionDigits: 0 });
  return (
    <div className="space-y-6">
      <SetupCard space="business" />
      <ConnectBank company={company} />
      <BizScoring company={company} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi to="/entreprise/depenses" label="Dépenses du mois" value={m0(o.total)} sub={o.delta_avg_pct == null ? "—" : `${o.delta_avg_pct > 0 ? "+" : ""}${o.delta_avg_pct} % vs moyenne 6 mois`} testid="biz-kpi-total" />
        <Kpi to="/entreprise/depenses" label="Encaissements du mois" value={m0(o.income)} sub={`Moyenne dépenses ${m0(o.avg6)}`} testid="biz-kpi-income" />
        <Kpi to="/entreprise/charges" label="Charges récurrentes" value={`${m0(d.recurring.monthly)}/mois`} sub={`Économie possible ${m0(d.recurring.savings_monthly)}/mois`} testid="biz-kpi-recurring" />
        <Kpi to="/entreprise/contrats" label="Assurances & prévoyance" value={`${m0(d.contracts.premium_year)}/an`} sub={`${d.contracts.count} contrat(s)`} testid="biz-kpi-contracts" />
      </div>
      <FiscalCalendar company={company} />
      <InterCompany company={company} />
      <BizBreakeven company={company} />
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card-surface space-y-3 p-5"><h2 className="flex items-center gap-2 font-bold"><TrendingUp className="h-4 w-4" /> Variations importantes (seuil {company.alert_threshold} %)</h2><AlertsList alerts={d.alerts} threshold={company.alert_threshold} /></section>
        <section className="card-surface space-y-3 p-5"><h2 className="flex items-center gap-2 font-bold"><CalendarClock className="h-4 w-4" /> Contrats à réévaluer</h2>
          {d.contracts.review_due.length ? <ul className="space-y-2" data-testid="biz-review-due">{d.contracts.review_due.map((c) => (
            <li key={c.id}><Link to="/entreprise/contrats" className="flex items-center gap-3 rounded-xl border border-border p-3 text-sm hover:border-primary/40"><ShieldCheck className="h-4 w-4" />
              <span className="min-w-0 flex-1"><b>{c.kind_label}</b><span className="block text-xs text-muted-foreground">{c.insurer} · échéance le {date(c.next_anniversary)} (J-{c.days_to_anniversary})</span></span><ArrowRight className="h-4 w-4" /></Link></li>))}</ul>
            : <p className="text-sm text-muted-foreground">Aucune échéance dans les 60 jours.</p>}
        </section>
      </div>
      <section className="card-surface p-5"><h2 className="mb-3 flex items-center gap-2 font-bold"><Repeat className="h-4 w-4" /> Dépenses et encaissements sur 7 mois</h2>
        <div className="h-56" data-testid="biz-trend"><ResponsiveContainer width="100%" height="100%"><ComposedChart data={o.trend.map((t) => ({ ...t, label: monthShort(t.month) }))} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
          <defs><Grad id="bzExp" color={C.coral} /><Grad id="bzInc" color={C.mint} from={0.35} to={0} /></defs>
          <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="currentColor" strokeOpacity={0.08} />
          <XAxis dataKey="label" {...axis} />
          <YAxis yAxisId="exp" hide /><YAxis yAxisId="inc" orientation="right" hide />
          <Tooltip cursor={cursor} content={<ChartTooltip names={{ total: "Dépenses", income: "Encaissements" }} fmt={m0} />} />
          <Area yAxisId="inc" type="monotone" dataKey="income" stroke={C.mint} strokeWidth={2.5} fill="url(#bzInc)" dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }} {...anim} />
          <Bar yAxisId="exp" dataKey="total" fill="url(#bzExp)" radius={[10, 10, 3, 3]} maxBarSize={30} {...anim} />
        </ComposedChart></ResponsiveContainer></div>
      </section>
    </div>
  );
};
